/**
 * Price Action Liquidity Sweeps Pro Engine (Frontend JS Engine)
 * Authentic implementation of Price Action - Liquidity Sweeps (Pine Script v5)
 * Licensed under CC BY-NC-SA 4.0 / © Price Action
 *
 * 1. Swings Pivot Detection: ta.pivothigh(len, len) and ta.pivotlow(len, len).
 * 2. Options:
 *    - 'Only Wicks': Liquidity sweeps through wicks, candle body closes back inside.
 *    - 'Only Outbreaks & Retest': Breakout with candle close, followed by pullback retest.
 *    - 'Wicks + Outbreaks & Retest': Combines both setups.
 * 3. Execution:
 *    - 'RETEST' (Default & High Win-Rate): Limit order placed exactly at the swept/broken pivot level.
 *    - 'MARKET': Immediate entry upon confirmation candle close.
 * 4. Sweep Area Boxes:
 *    - Dynamic boxes extended until broken or max bars reached.
 *    - Width 3 rejection wick line + dotted notch line.
 */

function getPipMultiplier(price) {
  if (price < 5.0) return 10000.0;
  if (price < 500.0) return 100.0;
  if (price < 15000.0) return 10.0;
  return 1.0;
}

function getPriceDecimals(price) {
  if (price < 5.0) return 5;
  if (price < 500.0) return 3;
  return 2;
}

function calcEma(arr, period) {
  const alpha = 2.0 / (period + 1.0);
  const ema = new Float64Array(arr.length);
  ema[0] = arr[0];
  for (let i = 1; i < arr.length; i++) {
    ema[i] = alpha * arr[i] + (1.0 - alpha) * ema[i - 1];
  }
  return ema;
}

function isInSession(tEpoch, startStr, endStr) {
  if (!startStr || !endStr || (startStr === "00:00" && (endStr === "23:59" || endStr === "24:00"))) {
    return true;
  }
  try {
    const d = new Date(tEpoch * 1000);
    const curMin = d.getUTCHours() * 60 + d.getUTCMinutes();
    const [sH, sM] = startStr.split(":").map(Number);
    const [eH, eM] = endStr.split(":").map(Number);
    const sMin = sH * 60 + sM;
    const eMin = eH * 60 + eM;
    if (sMin <= eMin) {
      return curMin >= sMin && curMin <= eMin;
    } else {
      return curMin >= sMin || curMin <= eMin;
    }
  } catch (e) {
    return true;
  }
}

export function calculateScalpingPro(bars, params = {}) {
  if (!bars || bars.length < 20) {
    return {
      signals: [],
      recent_signals: [],
      all_signals: [],
      latest_signal: null,
      chart_markers: [],
      visual_lines: [],
      sweep_boxes: [],
      liquidity_levels: [],
      market_bias: "NEUTRAL",
      streaks: { max_win_streak: 0, max_loss_streak: 0 },
      backtest: { total_trades: 0, wins: 0, losses: 0, win_rate: 0, profit_factor: 0, net_pnl: 0, max_drawdown: 0 },
      backtest_windows: {},
    };
  }

  const length = Math.max(1, Math.min(Number(params.scalpLookback ?? params.scalp_lookback ?? params.sweepLookback ?? params.sweep_lookback ?? params.len ?? 5), 50));
  // Pure Breakout Strategy (Chỉ đánh phá vỡ cản & Retest, loại bỏ hoàn toàn quét râu nến)
  const opt = "Only Outbreaks & Retest";

  const extendBoxes = Boolean(params.scalpExtend ?? params.scalp_extend ?? params.sweepExtend ?? params.extend ?? true);
  const maxB = Math.max(1, Math.min(Number(params.scalpMaxBars ?? params.scalp_max_bars ?? params.sweepMaxBars ?? params.sweep_max_bars ?? params.maxB ?? 300), 5000));

  let entryMode = String(params.scalpEntryMode ?? params.scalp_entry_mode ?? params.sweepEntryMode ?? params.sweep_entry_mode ?? params.entryMode ?? "RETEST").toUpperCase();
  if (entryMode.includes("RETEST") || entryMode.includes("LIMIT")) {
    entryMode = "RETEST";
  } else {
    entryMode = "MARKET";
  }

  const rr = Math.max(0.5, Math.min(Number(params.scalpRr ?? params.scalp_rr ?? params.sweepRr ?? params.sweep_rr ?? params.rr ?? 2.0), 10.0));
  const slBuf = Math.max(0.0, Math.min(Number(params.scalpSlBufferPips ?? params.scalp_sl_buffer_pips ?? params.sweepSlBufferPips ?? params.sweep_sl_buffer_pips ?? 1.5), 50.0));
  const minRisk = Math.max(0.5, Number(params.scalpMinRiskPips ?? params.scalp_min_risk_pips ?? params.sweepMinRiskPips ?? params.min_risk_pips ?? 3.0));
  const maxRisk = Math.max(minRisk, Number(params.scalpMaxRiskPips ?? params.scalp_max_risk_pips ?? params.sweepMaxRiskPips ?? params.max_risk_pips ?? 150.0));
  const cooldown = Math.max(1, Number(params.scalpCooldownBars ?? params.scalp_cooldown_bars ?? params.sweepCooldownBars ?? params.cooldown_bars ?? 3));
  const minSwingPips = Number(params.scalpMinSwingPips ?? params.scalp_min_swing_pips ?? params.sweepMinSwingPips ?? params.sweep_min_swing_pips ?? params.min_swing_pips ?? 25.0);

  const useTrend = Boolean(params.scalpTrendFilterEnable ?? params.sweepTrendFilterEnable ?? params.trend_filter_enable ?? false);
  const emaPeriod = Math.max(10, Number(params.scalpTrendEmaPeriod ?? params.sweepTrendEmaPeriod ?? 200));

  const useSession = Boolean(params.scalpSessionFilterEnable ?? params.sweepSessionFilterEnable ?? false);
  const sessStart = params.scalpSessionStart ?? params.sweepSessionStart ?? "00:00";
  const sessEnd = params.scalpSessionEnd ?? params.sweepSessionEnd ?? "23:59";
  const cancelBars = Math.max(5, Number(params.scalpLimitCancelBars ?? params.scalp_limit_cancel_bars ?? params.sweepLimitCancelBars ?? params.cancelBars ?? 30));

  const n = bars.length;
  const times = new Float64Array(n);
  const opens = new Float64Array(n);
  const highs = new Float64Array(n);
  const lows = new Float64Array(n);
  const closes = new Float64Array(n);

  for (let i = 0; i < n; i++) {
    times[i] = Number(bars[i].time);
    opens[i] = Number(bars[i].open);
    highs[i] = Number(bars[i].high);
    lows[i] = Number(bars[i].low);
    closes[i] = Number(bars[i].close);
  }

  const currentPrice = closes[n - 1];
  const pipMult = getPipMultiplier(currentPrice);
  const decimals = getPriceDecimals(currentPrice);

  const ema = useTrend ? calcEma(closes, emaPeriod) : null;

  const oW = (opt === "Only Wicks");
  const oO = (opt === "Only Outbreaks & Retest");

  const aPivH = [];
  const aPivL = [];
  const rawBoxes = [];
  const visualLines = [];
  const chartMarkers = [];
  const rawTrades = [];

  let lastTradeBar = -999;

  for (let i = length * 2; i < n; i++) {
    const pivIdx = i - length;

    // 1. Pivot High detection: ta.pivothigh(len, len) with strict symmetry & prominence
    let isPh = true;
    const pValH = highs[pivIdx];
    for (let j = pivIdx - length; j <= pivIdx + length; j++) {
      if (j === pivIdx) continue;
      if (j < pivIdx) {
        if (highs[j] >= pValH) {
          isPh = false;
          break;
        }
      } else {
        if (highs[j] > pValH) {
          isPh = false;
          break;
        }
      }
    }
    if (isPh) {
      let winMin = pValH;
      for (let k = pivIdx - length; k <= pivIdx + length; k++) {
        if (lows[k] < winMin) winMin = lows[k];
      }
      if ((pValH - winMin) * pipMult >= minSwingPips) {
        if (!aPivH.some(p => p.state === 0 && Math.abs(p.prc - pValH) * pipMult < 15.0)) {
          aPivH.unshift({
            prc: pValH,
            bix: pivIdx,
            time: times[pivIdx],
            state: 0, // 0=IDLE, 1=SWEPT, 2=KILLED_NGUOC, 3=CONFIRMED, 4=INVALIDATED
            sweep_bar: -1,
            sweep_close: 0,
            sweep_extreme: pValH,
            wait_bars: 0,
            is_wick: false,
          });
        }
      }
    }

    // 2. Pivot Low detection: ta.pivotlow(len, len) with strict symmetry & prominence
    let isPl = true;
    const pValL = lows[pivIdx];
    for (let j = pivIdx - length; j <= pivIdx + length; j++) {
      if (j === pivIdx) continue;
      if (j < pivIdx) {
        if (lows[j] <= pValL) {
          isPl = false;
          break;
        }
      } else {
        if (lows[j] < pValL) {
          isPl = false;
          break;
        }
      }
    }
    if (isPl) {
      let winMax = pValL;
      for (let k = pivIdx - length; k <= pivIdx + length; k++) {
        if (highs[k] > winMax) winMax = highs[k];
      }
      if ((winMax - pValL) * pipMult >= minSwingPips) {
        if (!aPivL.some(p => p.state === 0 && Math.abs(p.prc - pValL) * pipMult < 15.0)) {
          aPivL.unshift({
            prc: pValL,
            bix: pivIdx,
            time: times[pivIdx],
            state: 0, // 0=IDLE, 1=SWEPT, 2=KILLED_NGUOC, 3=CONFIRMED, 4=INVALIDATED
            sweep_bar: -1,
            sweep_close: 0,
            sweep_extreme: pValL,
            wait_bars: 0,
            is_wick: false,
          });
        }
      }
    }

    const curOpen = opens[i];
    const curClose = closes[i];
    const curHigh = highs[i];
    const curLow = lows[i];
    const curTime = times[i];

    const inSession = useSession ? isInSession(curTime, sessStart, sessEnd) : true;

    // 3. Process aPivH (Pivot Highs - SELL Setup: Sweep High -> Kill Nguoc -> Close below)
    const delH = [];
    let candleSweptH = false;
    for (let idx = aPivH.length - 1; idx >= 0; idx--) {
      const get = aPivH[idx];
      if (get.state === 0) {
        // User's rule: "cây nến vừa đóng qua" -> candle closes above key high
        if (curClose > get.prc) {
          get.state = 1;
          get.sweep_bar = i;
          get.sweep_close = curClose;
          get.sweep_high = curHigh;
          get.sweep_low = curLow;
          get.sweep_extreme = curHigh;
          get.wait_bars = 0;
          get.is_wick = false;
        }
      } else if (get.state === 1 || get.state === 2) {
        get.wait_bars += 1;
        get.sweep_extreme = Math.max(get.sweep_extreme, curHigh);

        // Step A: Kill Nguoc check at bar + 1 ("ko có cây nến thứ 2 nào đóng cao hơn cây nến đó nữa")
        if (get.wait_bars === 1) {
          if (curClose > get.sweep_close) {
            // Breakout continuing up -> Invalidated!
            get.state = 4;
            delH.push(idx);
            continue;
          } else {
            get.state = 2; // Kill Nguoc validated!
          }
        }

        // Step B: Confirmation Candle check:
        // User's rule: nến phải đóng ngược lại (ko tính rút râu), nến đỏ đảo chiều đóng dưới đỉnh
        if (get.state === 2 && get.wait_bars >= 1 && get.wait_bars <= 5) {
          const isBearish = curClose < curOpen && curClose < get.sweep_close;
          const isReclaimed = curClose < get.prc;
          if (isBearish && isReclaimed) {
            // SWEEP CONFIRMED!
            get.state = 3;
            if (!candleSweptH) {
              candleSweptH = true;
              // Visual Box (Bearish: top=sweep_extreme, bottom=pivot)
              rawBoxes.unshift({
                direction: 1,
                left_bix: get.sweep_bar,
                left_time: times[get.sweep_bar],
                right_bix: i,
                right_time: curTime,
                top: Number(get.sweep_extreme.toFixed(decimals)),
                bottom: Number(get.prc.toFixed(decimals)),
                broken: false,
                sweep_bix: get.sweep_bar,
                sweep_time: times[get.sweep_bar],
                rejection_y1: Number(get.sweep_extreme.toFixed(decimals)),
                rejection_y2: Number(get.prc.toFixed(decimals)),
              });

              // Dotted line from pivot to sweep bar
              visualLines.push({
                x1: get.time,
                x2: times[get.sweep_bar],
                y1: Number(get.prc.toFixed(decimals)),
                y2: Number(get.prc.toFixed(decimals)),
                color: "#f23645",
                style: 1, // Dotted
                width: 1.0,
              });

              // Trade Signal Generation
              if (inSession && (!useTrend || (ema && curClose <= ema[i]))) {
                if (i - lastTradeBar >= cooldown) {
                  const entryPrc = entryMode === "RETEST" ? get.prc : curClose;
                  const slPrc = get.sweep_extreme + slBuf / pipMult;
                  const risk = (slPrc - entryPrc) * pipMult;
                  if (risk >= minRisk && risk <= maxRisk) {
                    const targetTp = entryPrc - (slPrc - entryPrc) * rr;
                    rawTrades.push({
                      bar_index: i,
                      time: curTime,
                      action: "SELL",
                      type: "SWEEP_KILL_REVERSAL",
                      entry_mode: entryMode,
                      piv_price: Number(get.prc.toFixed(decimals)),
                      entry: Number(entryPrc.toFixed(decimals)),
                      price: Number(entryPrc.toFixed(decimals)),
                      sl: Number(slPrc.toFixed(decimals)),
                      tp: Number(targetTp.toFixed(decimals)),
                      tp1: Number(targetTp.toFixed(decimals)),
                      tp2: Number(targetTp.toFixed(decimals)),
                      risk_pips: Number(risk.toFixed(1)),
                      sweep_high: Number(get.sweep_extreme.toFixed(decimals)),
                      sweep_low: Number(get.prc.toFixed(decimals)),
                      cancel_bars: cancelBars,
                    });
                    lastTradeBar = i;
                    chartMarkers.push({
                      time: curTime,
                      position: "aboveBar",
                      color: "#f23645",
                      shape: "arrowDown",
                      text: "SELL",
                    });
                  }
                }
              }
            }
            delH.push(idx);
            continue;
          }
        } else if (get.wait_bars > 5) {
          get.state = 4;
          delH.push(idx);
        }
      }

      if (i - get.bix > 2000 || get.state === 3 || get.state === 4) {
        if (!delH.includes(idx)) {
          delH.push(idx);
        }
      }
    }

    for (let d = 0; d < delH.length; d++) {
      aPivH.splice(delH[d], 1);
    }

    // 4. Process aPivL (Pivot Lows - BUY Setup: Sweep Low -> Kill Nguoc -> Close above)
    const delL = [];
    let candleSweptL = false;
    for (let idx = aPivL.length - 1; idx >= 0; idx--) {
      const get = aPivL[idx];
      if (get.state === 0) {
        // User's rule: "cây nến vừa đóng qua" -> candle closes below key low
        if (curClose < get.prc) {
          get.state = 1;
          get.sweep_bar = i;
          get.sweep_close = curClose;
          get.sweep_high = curHigh;
          get.sweep_low = curLow;
          get.sweep_extreme = curLow;
          get.wait_bars = 0;
          get.is_wick = false;
        }
      } else if (get.state === 1 || get.state === 2) {
        get.wait_bars += 1;
        get.sweep_extreme = Math.min(get.sweep_extreme, curLow);

        // Step A: Kill Nguoc check at bar + 1 ("ko có cây nến thứ 2 nào đóng thấp hơn cây nến đó nữa")
        if (get.wait_bars === 1) {
          if (curClose < get.sweep_close) {
            // Breakdown continuing down -> Invalidated!
            get.state = 4;
            delL.push(idx);
            continue;
          } else {
            get.state = 2; // Kill Nguoc validated!
          }
        }

        // Step B: Confirmation Candle check:
        // User's rule: nến phải đóng ngược lên, nến xanh đảo chiều đóng trên đáy
        if (get.state === 2 && get.wait_bars >= 1 && get.wait_bars <= 5) {
          const isBullish = curClose > curOpen && curClose > get.sweep_close;
          const isReclaimed = curClose > get.prc;
          if (isBullish && isReclaimed) {
            // SWEEP CONFIRMED!
            get.state = 3;
            if (!candleSweptL) {
              candleSweptL = true;
              // Visual Box (Bullish: top=pivot, bottom=sweep_extreme)
              rawBoxes.unshift({
                direction: -1,
                left_bix: get.sweep_bar,
                left_time: times[get.sweep_bar],
                right_bix: i,
                right_time: curTime,
                top: Number(get.prc.toFixed(decimals)),
                bottom: Number(get.sweep_extreme.toFixed(decimals)),
                broken: false,
                sweep_bix: get.sweep_bar,
                sweep_time: times[get.sweep_bar],
                rejection_y1: Number(get.prc.toFixed(decimals)),
                rejection_y2: Number(get.sweep_extreme.toFixed(decimals)),
              });

              // Dotted line from pivot to sweep bar
              visualLines.push({
                x1: get.time,
                x2: times[get.sweep_bar],
                y1: Number(get.prc.toFixed(decimals)),
                y2: Number(get.prc.toFixed(decimals)),
                color: "#089981",
                style: 1, // Dotted
                width: 1.0,
              });

              // Trade Signal Generation
              if (inSession && (!useTrend || (ema && curClose >= ema[i]))) {
                if (i - lastTradeBar >= cooldown) {
                  const entryPrc = entryMode === "RETEST" ? get.prc : curClose;
                  const slPrc = get.sweep_extreme - slBuf / pipMult;
                  const risk = (entryPrc - slPrc) * pipMult;
                  if (risk >= minRisk && risk <= maxRisk) {
                    const targetTp = entryPrc + (entryPrc - slPrc) * rr;
                    rawTrades.push({
                      bar_index: i,
                      time: curTime,
                      action: "BUY",
                      type: "SWEEP_KILL_REVERSAL",
                      entry_mode: entryMode,
                      piv_price: Number(get.prc.toFixed(decimals)),
                      entry: Number(entryPrc.toFixed(decimals)),
                      price: Number(entryPrc.toFixed(decimals)),
                      sl: Number(slPrc.toFixed(decimals)),
                      tp: Number(targetTp.toFixed(decimals)),
                      tp1: Number(targetTp.toFixed(decimals)),
                      tp2: Number(targetTp.toFixed(decimals)),
                      risk_pips: Number(risk.toFixed(1)),
                      sweep_high: Number(get.prc.toFixed(decimals)),
                      sweep_low: Number(get.sweep_extreme.toFixed(decimals)),
                      cancel_bars: cancelBars,
                    });
                    lastTradeBar = i;
                    chartMarkers.push({
                      time: curTime,
                      position: "belowBar",
                      color: "#089981",
                      shape: "arrowUp",
                      text: "BUY",
                    });
                  }
                }
              }
            }
            delL.push(idx);
            continue;
          }
        } else if (get.wait_bars > 5) {
          get.state = 4;
          delL.push(idx);
        }
      }

      if (i - get.bix > 2000 || get.state === 3 || get.state === 4) {
        if (!delL.includes(idx)) {
          delL.push(idx);
        }
      }
    }

    for (let d = 0; d < delL.length; d++) {
      aPivL.splice(delL[d], 1);
    }

    // 5. Extend Sweep Boxes
    if (extendBoxes) {
      for (let b = 0; b < rawBoxes.length; b++) {
        const bx = rawBoxes[b];
        if (!bx.broken && i - bx.left_bix - 1 <= maxB) {
          bx.right_bix = i;
          bx.right_time = times[i];
          if (bx.direction === -1 && curClose < bx.bottom) {
            bx.broken = true;
          } else if (bx.direction === 1 && curClose > bx.top) {
            bx.broken = true;
          }
        }
      }
    }
  }

  // Compile visual sweep boxes:
  // Keep unbroken active zones (up to 5) + most recent trade setup zone to avoid chart clutter
  const unbrokenBoxes = rawBoxes.filter((b) => !b.broken);
  const displayBoxes = unbrokenBoxes.slice(0, 5);
  if (rawBoxes.length > 0 && !displayBoxes.some((b) => b.left_bix === rawBoxes[0].left_bix)) {
    displayBoxes.unshift(rawBoxes[0]);
  }

  const sweepBoxes = displayBoxes.map((bx) => {
    const isBull = bx.direction === -1;
    return {
      direction: bx.direction,
      left_time: bx.left_time,
      right_time: bx.right_time,
      top: bx.top,
      bottom: bx.bottom,
      broken: bx.broken,
      color: isBull ? "#08998126" : "#f2364526",
      border_color: isBull ? "#08998180" : "#f2364580",
      sweep_bar_time: bx.sweep_time,
      rejection_y1: bx.rejection_y1,
      rejection_y2: bx.rejection_y2,
    };
  });

  // Do NOT export unmitigated levels to prevent dashed horizontal stripe clutter
  const liquidityLevels = [];

  // 6. Forward Trade Evaluation
  const completedTrades = [];
  let wins = 0;
  let losses = 0;
  let grossProfit = 0.0;
  let grossLoss = 0.0;
  let maxWinStreak = 0;
  let maxLossStreak = 0;
  let curWinStreak = 0;
  let curLossStreak = 0;

  for (let t = 0; t < rawTrades.length; t++) {
    const trade = { ...rawTrades[t] };
    const barIdx = trade.bar_index;
    const act = trade.action;
    const entry = trade.entry;
    const sl = trade.sl;
    const tp2 = trade.tp2;
    const risk = trade.risk_pips;
    const mode = trade.entry_mode;
    const cLimit = trade.cancel_bars;

    let status = "PENDING";
    let fillBar = -1;
    let exitBar = -1;
    let exitPrice = entry;
    let pnlPips = 0.0;
    let rMult = 0.0;

    if (mode === "RETEST") {
      let filled = false;
      const maxFillBar = Math.min(n, barIdx + 1 + cLimit);
      for (let f = barIdx + 1; f < maxFillBar; f++) {
        if (act === "BUY" && lows[f] <= entry) {
          filled = true;
          fillBar = f;
          break;
        } else if (act === "SELL" && highs[f] >= entry) {
          filled = true;
          fillBar = f;
          break;
        }
        if (act === "BUY" && lows[f] <= sl) {
          status = "CANCELED";
          break;
        } else if (act === "SELL" && highs[f] >= sl) {
          status = "CANCELED";
          break;
        }
      }

      if (status === "CANCELED") {
        trade.status = "CANCELED";
        trade.pnl_pips = 0.0;
        completedTrades.push(trade);
        continue;
      }

      if (!filled) {
        if (n - 1 - barIdx >= cLimit) {
          trade.status = "EXPIRED";
          trade.pnl_pips = 0.0;
        } else {
          trade.status = "PENDING";
          trade.pnl_pips = 0.0;
        }
        completedTrades.push(trade);
        continue;
      }

      var startEval = fillBar;
    } else {
      var startEval = barIdx + 1;
    }

    status = "OPEN";
    for (let k = startEval; k < n; k++) {
      if (act === "BUY") {
        if (lows[k] <= sl) {
          status = "LOSS_SL";
          exitBar = k;
          exitPrice = sl;
          pnlPips = -risk;
          rMult = -1.0;
          break;
        } else if (highs[k] >= tp2) {
          status = "WIN_TP2";
          exitBar = k;
          exitPrice = tp2;
          pnlPips = risk * rr;
          rMult = rr;
          break;
        }
      } else {
        if (highs[k] >= sl) {
          status = "LOSS_SL";
          exitBar = k;
          exitPrice = sl;
          pnlPips = -risk;
          rMult = -1.0;
          break;
        } else if (lows[k] <= tp2) {
          status = "WIN_TP2";
          exitBar = k;
          exitPrice = tp2;
          pnlPips = risk * rr;
          rMult = rr;
          break;
        }
      }
    }

    if (status === "OPEN") {
      const unrealized = act === "BUY" ? (currentPrice - entry) * pipMult : (entry - currentPrice) * pipMult;
      trade.status = "OPEN";
      trade.pnl_pips = Number(unrealized.toFixed(1));
      trade.r_multiple = risk > 0 ? Number((unrealized / risk).toFixed(2)) : 0.0;
    } else {
      trade.status = status;
      trade.exit_bar = exitBar;
      trade.exit_time = exitBar >= 0 && exitBar < n ? times[exitBar] : times[n - 1];
      trade.exit_price = Number(exitPrice.toFixed(decimals));
      trade.pnl_pips = Number(pnlPips.toFixed(1));
      trade.r_multiple = Number(rMult.toFixed(2));

      if (status.includes("WIN")) {
        wins++;
        grossProfit += pnlPips;
        curWinStreak++;
        curLossStreak = 0;
        if (curWinStreak > maxWinStreak) maxWinStreak = curWinStreak;
      } else if (status.includes("LOSS")) {
        losses++;
        grossLoss += Math.abs(pnlPips);
        curLossStreak++;
        curWinStreak = 0;
        if (curLossStreak > maxLossStreak) maxLossStreak = curLossStreak;
      }
    }

    completedTrades.push(trade);
  }

  const totalClosed = wins + losses;
  const winRate = totalClosed > 0 ? Number(((wins / totalClosed) * 100.0).toFixed(1)) : 0.0;
  const profitFactor = grossLoss > 0 ? Number((grossProfit / grossLoss).toFixed(2)) : grossProfit > 0 ? 99.0 : 0.0;
  const netPnl = Number((grossProfit - grossLoss).toFixed(1));

  let equity = 0.0;
  let peak = 0.0;
  let maxDd = 0.0;
  for (let t = 0; t < completedTrades.length; t++) {
    if (["WIN_TP2", "WIN_TP1", "LOSS_SL"].includes(completedTrades[t].status)) {
      equity += completedTrades[t].pnl_pips;
      if (equity > peak) peak = equity;
      const dd = peak - equity;
      if (dd > maxDd) maxDd = dd;
    }
  }

  const backtestStats = {
    total_trades: completedTrades.length,
    closed_trades: totalClosed,
    wins,
    losses,
    win_rate: winRate,
    profit_factor: profitFactor,
    net_pnl: netPnl,
    max_drawdown: Number(maxDd.toFixed(1)),
    gross_profit: Number(grossProfit.toFixed(1)),
    gross_loss: Number(grossLoss.toFixed(1)),
  };

  const nowTs = times[n - 1];
  const windows = {
    "24h": nowTs - 86400,
    "3d": nowTs - 86400 * 3,
    "7d": nowTs - 86400 * 7,
    "30d": nowTs - 86400 * 30,
    all: 0,
  };
  const backtestWindows = {};
  for (const [wName, wStart] of Object.entries(windows)) {
    const wTrades = completedTrades.filter(
      (t) => t.time >= wStart && ["WIN_TP2", "WIN_TP1", "LOSS_SL"].includes(t.status)
    );
    const wWins = wTrades.filter((t) => t.status.includes("WIN") || t.pnl_pips > 0).length;
    const wLosses = wTrades.filter((t) => t.status.includes("LOSS") || t.pnl_pips < 0).length;
    const wTot = wWins + wLosses;
    const wGp = wTrades.filter((t) => t.status.includes("WIN") || t.pnl_pips > 0).reduce((acc, t) => acc + t.pnl_pips, 0);
    const wGl = wTrades.filter((t) => t.status.includes("LOSS") || t.pnl_pips < 0).reduce((acc, t) => acc + Math.abs(t.pnl_pips), 0);
    let curW = 0, curL = 0, wMaxW = 0, wMaxL = 0;
    wTrades.forEach((t) => {
      const isWin = t.status?.includes("WIN") || t.pnl_pips > 0;
      const isLoss = t.status?.includes("LOSS") || t.pnl_pips < 0;
      if (isWin) {
        curW++; curL = 0;
        if (curW > wMaxW) wMaxW = curW;
      } else if (isLoss) {
        curL++; curW = 0;
        if (curL > wMaxL) wMaxL = curL;
      } else {
        curW = 0; curL = 0;
      }
    });
    backtestWindows[wName] = {
      trades: wTot,
      wins: wWins,
      losses: wLosses,
      winRate: wTot > 0 ? Number(((wWins / wTot) * 100.0).toFixed(1)) : 0.0,
      profitFactor: wGl > 0 ? Number((wGp / wGl).toFixed(2)) : wGp > 0 ? 99.0 : 0.0,
      netPips: Number((wGp - wGl).toFixed(1)),
      max_win_streak: wMaxW,
      max_loss_streak: wMaxL,
      streaks: { max_win_streak: wMaxW, max_loss_streak: wMaxL },
      trades: wTrades,
    };
  }

  let latestSignal = null;
  if (completedTrades.length > 0) {
    latestSignal = completedTrades[completedTrades.length - 1];
  }

  // Draw Trade Lines for Active/Pending signal so chart is clear and never frozen
  if (latestSignal && ["OPEN", "PENDING"].includes(latestSignal.status)) {
    const sigTime = latestSignal.time;
    const entryP = latestSignal.entry;
    const slP = latestSignal.sl;
    const tp2P = latestSignal.tp2;

    visualLines.push({
      x1: sigTime,
      x2: times[n - 1],
      y1: entryP,
      y2: entryP,
      color: "#8b5cf6",
      style: 0,
      width: 1.5,
      label: `Entry: ${entryP.toFixed(2)}`,
    });
    visualLines.push({
      x1: sigTime,
      x2: times[n - 1],
      y1: slP,
      y2: slP,
      color: "#ef4444",
      style: 2,
      width: 1.5,
      label: `SL: ${slP.toFixed(2)}`,
    });
    visualLines.push({
      x1: sigTime,
      x2: times[n - 1],
      y1: tp2P,
      y2: tp2P,
      color: "#10b981",
      style: 2,
      width: 1.5,
      label: `TP (${rr.toFixed(1)}R): ${tp2P.toFixed(2)}`,
    });
  }

  let bias = "NEUTRAL";
  if (closes.length >= 50) {
    if (currentPrice > closes[closes.length - 20]) bias = "BULLISH";
    else if (currentPrice < closes[closes.length - 20]) bias = "BEARISH";
  }

  return {
    signals: completedTrades.slice(-50),
    recent_signals: completedTrades.slice(-50),
    all_signals: completedTrades,
    latest_signal: latestSignal,
    chart_markers: chartMarkers.slice(-100),
    visual_lines: visualLines.slice(-200),
    sweep_boxes: sweepBoxes,
    liquidity_levels: liquidityLevels.slice(0, 40),
    market_bias: bias,
    streaks: {
      max_win_streak: maxWinStreak,
      max_loss_streak: maxLossStreak,
    },
    backtest: backtestStats,
    backtest_windows: backtestWindows,
  };
}
